
import { generateExercisesForLevel } from '@/utils/exerciseUtils';
import { completeLessonAndAwardXP } from './useLessonCompletionHandler';
import { LevelConfig } from '@/models/LearnTypes';
import { Lesson } from '@/models/Lesson';
import { MutationHooks } from '../useLevelProgression.types';

interface UseLevelNavigationProps {
  userId: string | undefined;
  levels: LevelConfig[];
  setLevels: React.Dispatch<React.SetStateAction<LevelConfig[]>>;
  currentLevelIndex: number;
  setCurrentLevelIndex: React.Dispatch<React.SetStateAction<number>>;
  totalXpGained: number;
  selectedLesson: Lesson | null;
  setShowLevelIntro: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLevelComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setShowLessonComplete: React.Dispatch<React.SetStateAction<boolean>>;
  setExercises: React.Dispatch<React.SetStateAction<any[]>>;
  resetExerciseState: (generatedExercises: any[]) => void;
  setPracticeMode: React.Dispatch<React.SetStateAction<boolean>>;
  mutations: MutationHooks;
  handleImperfectLesson?: () => void; // Add parameter for marking lesson as imperfect
}

export const useLevelNavigation = ({
  userId,
  levels,
  setLevels,
  currentLevelIndex,
  setCurrentLevelIndex,
  totalXpGained,
  selectedLesson,
  setShowLevelIntro,
  setShowLevelComplete,
  setShowLessonComplete,
  setExercises,
  resetExerciseState,
  setPracticeMode,
  mutations,
  handleImperfectLesson
}: UseLevelNavigationProps) => {
  
  const moveToNextLevel = () => {
    setShowLevelComplete(false);
    setPracticeMode(false); // Reset practice mode
    
    const nextLevelIndex = currentLevelIndex + 1;
    
    if (nextLevelIndex < levels.length) {
      prepareNextLevel(nextLevelIndex);
    } else {
      completeLessonAndAwardXP(
        userId,
        selectedLesson,
        totalXpGained,
        setShowLessonComplete,
        mutations
      );
    }
  };
  
  const skipToNextLevel = () => {
    setShowLevelIntro(false);
    setPracticeMode(false); // Reset practice mode
    
    // Set global flag to track that a level was skipped
    (window as any).__LEVEL_SKIPPED__ = true;
    
    // Store in localStorage for persistence
    if (selectedLesson?.id) {
      try {
        localStorage.setItem(`lesson_${selectedLesson.id}_level_skipped`, 'true');
        console.debug(`🚩 [skipToNextLevel] Stored level skipped status in localStorage for lesson ${selectedLesson.id}`);
      } catch (e) {
        console.error('Error setting localStorage:', e);
      }
    }
    
    // Mark the lesson as imperfect when a level is skipped
    if (handleImperfectLesson) {
      console.debug(`🏆 [skipToNextLevel] Level ${currentLevelIndex} was skipped. Marking lesson as imperfect.`);
      handleImperfectLesson();
    } else {
      console.warn(`🏆 [skipToNextLevel] handleImperfectLesson function is not available. Cannot mark lesson as imperfect.`);
    }
    
    const nextLevelIndex = currentLevelIndex + 1;
    
    // Mark current level as completed in levels state without awarding XP
    const newLevels = [...levels];
    newLevels[currentLevelIndex].state = 'completed';
    setLevels(newLevels);
    
    if (nextLevelIndex < levels.length) {
      prepareNextLevel(nextLevelIndex);
    } else {
      completeLessonAndAwardXP(
        userId,
        selectedLesson,
        totalXpGained,
        setShowLessonComplete,
        mutations
      );
    }
  };
  
  const prepareNextLevel = (nextLevelIndex: number) => {
    console.log(`Moving to next level: ${nextLevelIndex + 1}`);
    
    // CRITICAL FIX: Clean up recycled words tracking when moving to a new level
    // This ensures recycled words from the previous level don't affect the new level
    
    // 1. Reset the global flags for perfect level tracking
    (window as any).__HAD_RECYCLED_WORDS__ = false;
    (window as any).__PERFECT_LEVEL_STATUS__ = undefined;
    console.debug(`⚠️ [prepareNextLevel] Reset global flags for new level ${nextLevelIndex}`);
    
    // 2. Clean up localStorage for the previous level if it exists
    // Since we're moving to a new level, we want to make sure localStorage is clean
    if (currentLevelIndex >= 0 && currentLevelIndex < levels.length) {
      const prevLevel = levels[currentLevelIndex];
      if (prevLevel?.id) {
        try {
          const prevLevelKey = `level_${prevLevel.id}_had_recycled_words`;
          localStorage.removeItem(prevLevelKey);
          console.debug(`🧹 [prepareNextLevel] Cleaned up localStorage for completed level: ${prevLevelKey}`);
        } catch (e) {
          console.error('Error cleaning localStorage:', e);
        }
      }
    }
    
    // 3. Prepare localStorage for the new level (set to false initially)
    const nextLevel = levels[nextLevelIndex];
    if (nextLevel?.id) {
      try {
        const nextLevelKey = `level_${nextLevel.id}_had_recycled_words`;
        localStorage.setItem(nextLevelKey, 'false');
        console.debug(`🔐 [prepareNextLevel] Initialized localStorage for new level: ${nextLevelKey}=false`);
      } catch (e) {
        console.error('Error initializing localStorage:', e);
      }
    }
    
    // Update level state and move to the next level
    const newLevels = [...levels];
    newLevels[nextLevelIndex].state = 'in-progress';
    setLevels(newLevels);
    setCurrentLevelIndex(nextLevelIndex);
    
    if (selectedLesson) {
      const nextLevelType = newLevels[nextLevelIndex].type;
      const generatedExercises = generateExercisesForLevel(nextLevelType, selectedLesson.words);
      
      resetExerciseState(generatedExercises);
    }
    
    setShowLevelIntro(true);
  };
  
  // Add the restartCurrentLevel function
  const restartCurrentLevel = (shouldAwardXP: boolean = false) => {
    console.log(`Restarting current level: ${currentLevelIndex + 1}`);
    
    // Reset practice mode
    setPracticeMode(false);
    
    // Reset the level's state
    const newLevels = [...levels];
    
    // Update UI states
    setShowLevelComplete(false);
    
    if (selectedLesson) {
      // Get the current level type and generate new exercises
      const levelType = newLevels[currentLevelIndex].type;
      const generatedExercises = generateExercisesForLevel(levelType, selectedLesson.words);
      
      // Reset exercise state
      resetExerciseState(generatedExercises);
    }
    
    setShowLevelIntro(true);
  };
  
  return {
    moveToNextLevel,
    skipToNextLevel,
    prepareNextLevel,
    restartCurrentLevel  // Add restartCurrentLevel to the returned object
  };
};
